<template>
    <VcModal
        :append-to="modalAppendToElement"
        :visible="visible"
        :header="i18n.t('callHistoryView.manageAi.addQuestionTitle')"
        :breakpoints="{
            '960px': '80vw',
            '780px': '90vw',
            '680px': '98vw'
        }"
        width="480px"
        @close="onCloseModal"
    >
        <div class="w-full">
            <div>
                <AlertMessageCard
                    v-if="showAlertMessage"
                    type="info"
                    :title="i18n.t('callHistoryView.manageAi.oneQuestionAllowedTitle')"
                    :message="i18n.t('callHistoryView.manageAi.oneQuestionAllowedMessage')"
                    class="mb-3 lg:mb-5"
                />
            </div>
            <AiGeneralQuestionForm
                ref="questionGeneralFormRef"
                v-model="questionModel"
                :used-keys="usedQuestionKeys"
            />
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between gap-2">
                <div
                    ref="optimizeBtnRef"
                    class="flex-center"
                >
                    <VcSplitButton
                        v-if="isButtonVisible"
                        :loading="optimizeLoading"
                        :disabled="isButtonDisabled"
                        :options="optimizePromptOptions"
                        :teleported="isTeleported"
                        color="primary-alternative"
                        @click="handleOptimizePrompt"
                    />
                </div>
                <div class="flex items-center gap-2">
                    <VcButton
                        type="outline"
                        color="secondary"
                        @click="onCloseModal"
                    >
                        {{ i18n.t('callHistoryView.cancel') }}
                    </VcButton>
                    <VcButton
                        :loading="loading"
                        color="primary"
                        @click="onSaveModal"
                    >
                        {{ i18n.t('callHistoryView.add') }}
                    </VcButton>
                </div>
            </div>
        </template>
    </VcModal>
</template>

<script setup lang="ts">
import { isTeleported, modalAppendToElement } from '@/services/GlobalState.ts'
import { ref } from 'vue'
import AiGeneralQuestionForm from '@/components/AiSettings/Forms/AiGeneralQuestionForm.vue'
import { IAiQuestionRequest } from '@/types/aiManager'
import { QUESTION_RELATION_TYPES } from '@/enums/aiManagement.enum.ts'
import AlertMessageCard from '@/components/Card/AlertMessageCard.vue'
import i18n from '@/plugins/i18n.ts'
import { VcSplitButton } from '@voicenter-team/voicenter-ui-plus'
import useOptimizeQuestionButton from '@/composables/useOptimizeQuestionButton.ts'

const DEFAULT_MODEL = {
    Key: '',
    Value: '',
    Source: '',
    QuestionRelationType: QUESTION_RELATION_TYPES.custom,
    AnswerDataTypeID: undefined,
    AnswerDataType: '',
    QuestionID: undefined,
    OriginalQuestionID: undefined,
}

defineProps<{
    usedQuestionKeys?: Array<{ Key: string, uid?: string | number }>
    showAlertMessage?: boolean
}>()
const emit = defineEmits<{
    (e: 'save', pl: IAiQuestionRequest): void
}>()

const loading = ref(false)
const questionGeneralFormRef = ref<typeof AiGeneralQuestionForm>()
const visible = ref(false)
const questionModel = ref<IAiQuestionRequest>({ ...DEFAULT_MODEL })
const optimizeBtnRef = ref()

/* Optimize prompt/schema button */
const {
    isButtonVisible,
    optimizeLoading,
    isButtonDisabled,
    optimizePromptOptions,
    handleOptimizePrompt
} = useOptimizeQuestionButton(questionModel, optimizeBtnRef)

function onCloseModal () {
    visible.value = false
    loading.value = false
    resetData()
}

function onOpenModal () {
    visible.value = true
}

function resetData () {
    questionModel.value = { ...DEFAULT_MODEL }
}

async function onSaveModal () {
    loading.value = true
    const validateResult = await questionGeneralFormRef.value?.validate()
    if (validateResult?.isValid) {
        emit('save', {
            ...questionModel.value,
            Key: questionModel.value.Key?.replace(/\s+/g, '_')
        })
    } else {
        loading.value = false
    }
}

defineExpose({
    open: onOpenModal,
    close: onCloseModal,
    setLoading: (state: boolean) => loading.value = state
})
</script>
